import { Button } from '@/components/ui/button';
import { cn, randomId, wait } from '@/lib/utils';
import type { PaymentResult } from '@dash-lite/sdk';
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    Elements,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe   } from '@stripe/stripe-js';
import type {Stripe, StripeElementStyle} from '@stripe/stripe-js';
import { CreditCard, Lock, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState  } from 'react';
import type {FormEvent, ReactNode} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DevNote } from '@/components/ui/dev-note';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardBody, CardHeader, Skeleton } from '@/components/ui/primitives';
import { useDeviceSessionId } from '@/hooks/use-device-session';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, isApiError, presentError } from '@/lib/errors';
import { describeOffer, headlinePrice } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Tunnel de paiement complet.
 *
 * Le parcours, identique pour les trois types d'offre :
 *
 * 1. `POST /payment` → `client_secret`, `publishable_key`, `payment_id` ;
 * 2. Stripe Elements est monté avec la **clé publishable renvoyée par l'API**
 *    (vous n'avez aucune clé Stripe à configurer côté front) ;
 * 3. le visiteur saisit sa carte **sur cette page** — l'API ne redirige jamais
 *    vers une page de paiement hébergée ;
 * 4. `stripe.confirmCardPayment()` confirme la carte (3-D Secure inclus) ;
 * 5. `POST /payment/claim` échange le paiement abouti contre un token, et
 *    provisionne le compte et le droit d'accès ;
 * 6. l'écran de confirmation lit `GET /entitlement`.
 *
 * Pourquoi l'étape 1 a-t-elle lieu **au chargement de la page** et non au clic
 * sur « Payer » ? Parce que Stripe Elements a besoin de la clé publishable
 * pour monter ses champs carte, et que cette clé arrive avec la réponse de
 * `POST /payment`. C'est la séquence recommandée : le formulaire est prêt
 * avant que le visiteur ne commence à saisir.
 *
 * Le header `Idempotency-Key` est envoyé sur `POST /payment`. Il est relayé à
 * Stripe et protège contre les doubles créations si le visiteur recharge la
 * page ou si le réseau bégaye.
 */

export function Checkout({
    /**
     * Contexte d'achat attaché au paiement, puis retrouvé sur le droit
     * d'accès. Au plus 10 clés, valeurs de 200 caractères. Visible dans le
     * back-office ET dans le compte Stripe du site : jamais de données
     * sensibles ici.
     */
    metadata,
}: {
    metadata?: Record<string, string>;
}) {
    const site = useSite();
    const { user } = useSession();
    const deviceSessionId = useDeviceSessionId();

    const [intent, setIntent] = useState<PaymentResult | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [creating, setCreating] = useState(true);

    /**
     * Clé d'idempotence de **cette tentative d'achat**. Elle est générée une
     * seule fois pour la durée de vie du composant : deux rendus successifs
     * ne créent pas deux paiements.
     */
    const idempotencyKey = useRef(randomId());

    /**
     * Empêche le double appel du `useEffect` en mode strict de React 19, qui
     * monte puis remonte les composants en développement. Sans cette garde,
     * `POST /payment` partirait deux fois à chaque chargement de page en dev.
     */
    const paymentRequested = useRef(false);

    useEffect(() => {
        if (paymentRequested.current) {
            return;
        }

        paymentRequested.current = true;

        async function createPayment(): Promise<void> {
            setCreating(true);
            setError(null);

            try {
                const result = await dashlite.createPayment(
                    {
                        // L'email est facultatif ici : il peut n'être fourni
                        // qu'au claim. On le pré-remplit pour un visiteur déjà
                        // connecté, dont le token identifie déjà le compte.
                        ...(user?.email ? { email: user.email } : {}),
                        ...(deviceSessionId ? { device_session_id: deviceSessionId } : {}),
                        ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
                    },
                    idempotencyKey.current,
                );

                setIntent(result);
            } catch (caught) {
                setError(caught);
            } finally {
                setCreating(false);
            }
        }

        void createPayment();
        // Volontairement sans dépendances : le paiement n'est créé qu'une fois.
        // L'empreinte d'appareil, qui arrive parfois après, reste facultative.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (creating) {
        return (
            <Card>
                <CardHeader title="Préparation du paiement" icon={<CreditCard className="size-4" />} />
                <CardBody className="space-y-3">
                    <Skeleton className="h-11 w-full" />
                    <Skeleton className="h-11 w-full" />
                    <Skeleton className="h-11 w-2/3" />
                </CardBody>
            </Card>
        );
    }

    if (error || !intent) {
        return <PaymentSetupError error={error} />;
    }

    return <StripeElementsShell intent={intent} offerLabel={site.offer ? describeOffer(site.offer) : null} />;
}

/**
 * Diagnostic lisible quand `POST /payment` échoue avant même l'affichage du
 * formulaire. Chaque code du catalogue appelle une réaction différente : ici,
 * elles sont explicitées plutôt que noyées dans un message générique.
 */
function PaymentSetupError({ error }: { error: unknown }) {
    const presentation = presentError(error);

    return (
        <Alert tone="critical" title={presentation.message}>
            <p>{presentation.hint}</p>

            {isApiError(error, 'offer_not_available') && (
                <p className="mt-2">
                    L'offre n'est pas synchronisée avec le compte Stripe du site pour cet environnement.{' '}
                    <button type="button" onClick={() => window.location.reload()} className="underline">
                        Recharger la page
                    </button>
                </p>
            )}

            {isApiError(error, 'account_exists') && (
                <p className="mt-2">
                    <Link to={paths.login} className="underline">
                        Se connecter
                    </Link>{' '}
                    ou{' '}
                    <Link to={paths.forgotPassword} className="underline">
                        définir un mot de passe
                    </Link>
                    .
                </p>
            )}

            {isApiError(error, 'payment_flagged_fraud') && (
                <p className="mt-2">
                    Aucun objet n'a été créé chez Stripe et aucun montant n'a été débité : il n'y a rien à annuler.
                </p>
            )}
        </Alert>
    );
}

/**
 * Monte Stripe Elements avec la clé publishable renvoyée par l'API.
 *
 * `loadStripe` est mémoïsé : le rappeler à chaque rendu rechargerait le script
 * Stripe et démonterait les champs carte en cours de saisie.
 */
function StripeElementsShell({ intent, offerLabel }: { intent: PaymentResult; offerLabel: string | null }) {
    const stripePromise = useMemo<Promise<Stripe | null>>(
        () => loadStripe(intent.publishable_key),
        [intent.publishable_key],
    );

    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret: intent.client_secret,
                locale: 'fr',
                appearance: {
                    theme: 'stripe',
                    variables: {
                        colorPrimary: '#3448a8',
                        colorText: '#1e2233',
                        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                        borderRadius: '8px',
                    },
                },
            }}
        >
            <CardPaymentForm intent={intent} offerLabel={offerLabel} />
        </Elements>
    );
}

/** Apparence des champs carte, alignée sur le thème du site. */
const CARD_FIELD_STYLE: StripeElementStyle = {
    base: {
        fontSize: '15px',
        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
        color: '#1e2233',
        '::placeholder': { color: '#8a8fa3' },
    },
    invalid: { color: '#c2410c', iconColor: '#c2410c' },
};

/**
 * Réseaux acceptés. Stripe reconnaît la marque dès les premiers chiffres du
 * numéro : la mettre en avant à ce moment-là rassure sans rien exiger.
 */
const CARD_BRANDS = [
    { key: 'visa', label: 'Visa' },
    { key: 'mastercard', label: 'Mastercard' },
    { key: 'amex', label: 'Amex' },
] as const;

/** État de saisie des trois champs carte, tous requis avant de payer. */
type CardCompletion = { number: boolean; expiry: boolean; cvc: boolean };

/**
 * Cadre d'un champ carte.
 *
 * Les champs Stripe vivent dans des iframes hébergées par Stripe : leur
 * contenu ne peut pas être stylé en CSS depuis cette page (c'est précisément
 * ce qui les met hors du périmètre PCI). Seule la bordure autour leur
 * appartient — l'intérieur est réglé par `CARD_FIELD_STYLE`, transmis à
 * Stripe.
 */
function CardFieldShell({
    label,
    children,
    trailing,
    className,
}: {
    label: string;
    children: ReactNode;
    trailing?: ReactNode;
    className?: string;
}) {
    return (
        <div className={cn('space-y-1.5', className)}>
            <span className="text-ink block text-sm font-medium">{label}</span>

            <div className="border-line-strong focus-within:border-brand-500 focus-within:ring-brand-500/20 flex items-center gap-3 rounded-lg border bg-white px-3.5 py-3.5 transition-colors focus-within:ring-2">
                <div className="min-w-0 flex-1">{children}</div>
                {trailing}
            </div>
        </div>
    );
}

function CardPaymentForm({ intent, offerLabel }: { intent: PaymentResult; offerLabel: string | null }) {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const site = useSite();
    const { user, adoptAuthResult } = useSession();

    const [email, setEmail] = useState(user?.email ?? '');
    const [submitting, setSubmitting] = useState(false);
    const [stage, setStage] = useState<'idle' | 'confirming' | 'claiming'>('idle');
    const [error, setError] = useState<unknown>(null);
    const [cardError, setCardError] = useState<string | null>(null);
    const [cardBrand, setCardBrand] = useState<string>('unknown');
    const [completion, setCompletion] = useState<CardCompletion>({ number: false, expiry: false, cvc: false });

    const fields = fieldErrors(error);

    // Les trois champs sont indépendants : le paiement n'est proposé que
    // lorsque Stripe les déclare tous complets.
    const cardComplete = completion.number && completion.expiry && completion.cvc;

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        // Champs éclatés : le numéro suffit à désigner le groupe d'éléments,
        // Stripe y récupère l'expiration et le cryptogramme.
        const card = elements.getElement(CardNumberElement);

        if (!card) {
            return;
        }

        setSubmitting(true);
        setError(null);
        setCardError(null);

        try {
            // --- Étape 3 : confirmation de la carte, côté navigateur --------
            // Les données de carte vont directement du navigateur à Stripe.
            // Elles ne transitent ni par ce front, ni par l'API Dash Lite.
            // L'authentification forte (3-D Secure) est prise en charge ici :
            // si la banque la réclame, Stripe l'affiche et la résout avant de
            // rendre la main.
            setStage('confirming');

            const confirmation = await stripe.confirmCardPayment(intent.client_secret, {
                payment_method: {
                    card,
                    billing_details: email ? { email } : undefined,
                },
            });

            if (confirmation.error) {
                // Carte refusée, expirée, 3-D Secure abandonné… Le message de
                // Stripe est déjà localisé et destiné au porteur de carte.
                setCardError(confirmation.error.message ?? 'La banque a refusé la transaction.');

                return;
            }

            // --- Étape 5 : réclamation du paiement --------------------------
            setStage('claiming');

            const auth = await claimWithRetry(intent.payment_id, email);
            adoptAuthResult(auth);

            navigate(paths.checkoutSuccess, { replace: true });
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
            setStage('idle');
        }
    }

    return (
        <div className="space-y-6">
            <Card className="overflow-hidden">
                <PaymentSummaryBand offerLabel={offerLabel} />

                <CardBody>
                    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                        <Input
                            label="Adresse email"
                            type="email"
                            required
                            autoComplete="email"
                            placeholder="vous@exemple.fr"
                            value={email}
                            error={fields.email}
                            onChange={(event) => setEmail(event.target.value)}
                            hint="Votre accès et votre reçu sont envoyés à cette adresse. Si vous n'avez pas encore de compte, il est créé automatiquement à la réussite du paiement."
                            // Repère du robot de vérification de mise en
                            // production. Invisible pour vos visiteurs.
                            data-dashlite="payment-email"
                        />

                        <div className="space-y-4">
                            <CardFieldShell
                                label="Numéro de carte"
                                trailing={
                                    <span aria-hidden className="flex shrink-0 items-center gap-1.5">
                                        {CARD_BRANDS.map((network) => (
                                            <span
                                                key={network.key}
                                                className={cn(
                                                    'border-line rounded border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase transition-colors',
                                                    cardBrand === network.key
                                                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                                                        : 'text-ink-muted/70',
                                                )}
                                            >
                                                {network.label}
                                            </span>
                                        ))}
                                    </span>
                                }
                            >
                                <CardNumberElement
                                    options={{ style: CARD_FIELD_STYLE, showIcon: false }}
                                    onChange={(event) => {
                                        setCardBrand(event.brand);
                                        setCompletion((previous) => ({ ...previous, number: event.complete }));
                                        setCardError(event.error?.message ?? null);
                                    }}
                                />
                            </CardFieldShell>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <CardFieldShell label="Expiration">
                                    <CardExpiryElement
                                        options={{ style: CARD_FIELD_STYLE }}
                                        onChange={(event) => {
                                            setCompletion((previous) => ({ ...previous, expiry: event.complete }));
                                            setCardError(event.error?.message ?? null);
                                        }}
                                    />
                                </CardFieldShell>

                                <CardFieldShell label="Cryptogramme">
                                    <CardCvcElement
                                        options={{ style: CARD_FIELD_STYLE }}
                                        onChange={(event) => {
                                            setCompletion((previous) => ({ ...previous, cvc: event.complete }));
                                            setCardError(event.error?.message ?? null);
                                        }}
                                    />
                                </CardFieldShell>
                            </div>

                            {cardError && (
                                <p role="alert" className="text-critical text-xs">
                                    {cardError}
                                </p>
                            )}

                            {site.environment === 'test' && (
                                <p className="text-ink-muted text-xs">
                                    Environnement de test — utilisez la carte{' '}
                                    <span className="font-mono">4242 4242 4242 4242</span>, n'importe quelle date
                                    future et n'importe quel cryptogramme.
                                </p>
                            )}
                        </div>

                        {error !== null && Object.keys(fields).length === 0 && <PaymentSubmitError error={error} />}

                        <RecurringCommitmentNotice />

                        <Button
                            type="submit"
                            size="lg"
                            className="w-full"
                            loading={submitting}
                            disabled={!stripe || !cardComplete || email.trim() === ''}
                            data-dashlite="payment-submit"
                        >
                            {stage === 'claiming'
                                ? 'Ouverture de votre accès…'
                                : stage === 'confirming'
                                  ? 'Confirmation auprès de votre banque…'
                                  : offerLabel
                                    ? `Payer ${site.offer ? headlinePrice(site.offer) : ''}`.trim()
                                    : 'Payer'}
                        </Button>

                        <div className="text-ink-muted space-y-1.5 text-center text-xs">
                            <p className="flex items-center justify-center gap-1.5">
                                <Lock className="size-3.5" aria-hidden />
                                Carte saisie sur cette page : aucune redirection vers un formulaire externe.
                            </p>
                            <p className="flex items-center justify-center gap-1.5">
                                <ShieldCheck className="size-3.5" aria-hidden />
                                Paiement traité par Stripe. Le contrôle anti-fraude est appliqué par la plateforme.
                            </p>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <DevNote
                title="Ce que fait cet écran"
                endpoints={['POST /payment', 'stripe.confirmCardPayment()', 'POST /payment/claim']}
            >
                <p>
                    <strong>Au chargement</strong>, <code className="font-mono">POST /payment</code> a renvoyé un{' '}
                    <code className="font-mono">client_secret</code>, la <code className="font-mono">publishable_key</code>{' '}
                    Stripe du site et un <code className="font-mono">payment_id</code> — traité comme opaque, il est
                    simplement renvoyé au claim.
                </p>
                <p>
                    <strong>À la validation</strong>, la carte est confirmée par Stripe depuis le navigateur, puis{' '}
                    <code className="font-mono">POST /payment/claim</code> échange le paiement abouti contre un token.
                    L'opération est idempotente et partagée avec le webhook Stripe : le premier des deux qui arrive
                    fait le travail.
                </p>
                <p>
                    Ouvrez l'inspecteur d'API pour voir la séquence complète, en-têtes compris.
                </p>
            </DevNote>
        </div>
    );
}

/**
 * Bandeau de récapitulatif, en tête du formulaire : ce qui est acheté à
 * gauche, ce qui sera prélevé à droite.
 *
 * Le montant vient de `GET /site`, jamais d'une constante recopiée dans le
 * front. Ce n'est pas un détail : le bot de vérification de mise en
 * production relève les prix affichés sur le site et les compare au prix
 * réellement prélevé — un écart est signalé comme un défaut, parce qu'il se
 * règle ensuite en remboursements.
 */
function PaymentSummaryBand({ offerLabel }: { offerLabel: string | null }) {
    const site = useSite();
    const offer = site.offer;

    if (!offer) {
        return null;
    }

    return (
        <div className="border-line bg-surface-sunken border-b px-5 py-5 sm:px-6">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="text-ink text-sm font-semibold">{offer.name}</p>
                <p className="text-ink text-xl font-semibold">{headlinePrice(offer)}</p>
            </div>

            <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                {/* La formule est écrite au même corps que le prix : c'est là
                    que se joue la différence entre informer et laisser
                    croire. Une durée d'essai ou un renouvellement en gris
                    minuscule sous un gros chiffre, c'est un prix caché. */}
                <p className="text-ink-soft text-sm">{offerLabel ?? 'Accès ouvert dès le paiement abouti.'}</p>
                <p className="text-ink-muted text-sm">prélevé aujourd'hui</p>
            </div>
        </div>
    );
}

/**
 * Ce que le visiteur autorise, dit avant qu'il l'autorise.
 *
 * Une offre récurrente engage au-delà du montant affiché en tête. L'écrire
 * ici, au moment du consentement et au corps du texte courant plutôt qu'en
 * gris minuscule, coûte trois lignes et évite la moitié des litiges. Le lien
 * vers la résiliation est donné maintenant, pas seulement dans l'email de
 * confirmation.
 */
function RecurringCommitmentNotice() {
    const site = useSite();
    const offer = site.offer;

    if (!offer || offer.type === 'one_time') {
        return null;
    }

    return (
        <p className="text-ink-soft text-xs leading-relaxed">
            En validant, vous souscrivez à : <strong className="text-ink font-medium">{describeOffer(offer)}</strong>.
            Le renouvellement est automatique jusqu'à résiliation,{' '}
            <Link to={paths.cancelSubscription} className="underline">
                possible en ligne
            </Link>{' '}
            à tout moment et sans justification.
        </p>
    );
}

/** Erreurs survenues après la confirmation de la carte. */
function PaymentSubmitError({ error }: { error: unknown }) {
    const presentation = presentError(error);

    return (
        <Alert tone="critical" title={presentation.message}>
            <p>{presentation.hint}</p>

            {isApiError(error, 'account_exists') && (
                <p className="mt-2">
                    <Link to={paths.login} className="underline">
                        Connectez-vous
                    </Link>{' '}
                    pour rattacher cet achat à votre compte existant.
                </p>
            )}
        </Alert>
    );
}

/**
 * Réclame le paiement, avec quelques tentatives espacées.
 *
 * Stripe peut mettre une fraction de seconde à marquer le paiement comme
 * abouti après `confirmCardPayment`. L'API répond alors
 * `422 validation_failed` (« ce paiement n'est pas encore abouti ») — le seul
 * cas où réessayer a du sens. Toute autre erreur remonte immédiatement.
 */
async function claimWithRetry(paymentId: string, email: string, attempts = 3) {
    let lastError: unknown;

    for (let attempt = 0; attempt < attempts; attempt++) {
        try {
            return await dashlite.claimPayment({
                payment_id: paymentId,
                ...(email ? { email } : {}),
            });
        } catch (error) {
            lastError = error;

            if (!isApiError(error, 'validation_failed') || attempt === attempts - 1) {
                throw error;
            }

            await wait(1200 * (attempt + 1));
        }
    }

    throw lastError;
}
